import { useState, useEffect } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Tags, Plus, Edit, Trash2, Loader2 } from "lucide-react";
import { extractErrorMessage } from "@/lib/errorMessage";

const TIPOS = [
  { value: "despesa", label: "Despesa", color: "bg-red-100 text-red-700" },
  { value: "receita", label: "Receita", color: "bg-emerald-100 text-emerald-700" },
  { value: "ambos", label: "Ambos", color: "bg-blue-100 text-blue-700" },
];

const COR_PALETA = [
  "#0A1628", "#C9A84C", "#10b981", "#3b82f6", "#f59e0b",
  "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6", "#64748b",
];

export default function CategoriasPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ nome: "", tipo: "despesa", cor: "#0A1628", descricao: "", ativo: true });
  const [deleteItem, setDeleteItem] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/categorias?apenas_ativas=false`);
      setItems(r.data || []);
    } catch {
      toast.error("Erro ao carregar categorias");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const resetForm = () => {
    setForm({ nome: "", tipo: "despesa", cor: "#0A1628", descricao: "", ativo: true });
    setEditId(null);
  };

  const openEdit = (c) => {
    setForm({ nome: c.nome, tipo: c.tipo, cor: c.cor || "#0A1628", descricao: c.descricao || "", ativo: c.ativo });
    setEditId(c.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.nome.trim()) { toast.error("Nome obrigatório"); return; }
    try {
      if (editId) {
        await axios.put(`${API}/categorias/${editId}`, form);
        toast.success("Categoria atualizada");
      } else {
        await axios.post(`${API}/categorias`, form);
        toast.success("Categoria criada");
      }
      setShowForm(false);
      resetForm();
      fetchAll();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro"));
    }
  };

  const handleDelete = async () => {
    if (!deleteItem) return;
    try {
      await axios.delete(`${API}/categorias/${deleteItem.id}`);
      toast.success("Categoria removida");
      setDeleteItem(null);
      fetchAll();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro"));
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#0A1628]" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="categorias-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold text-[#0A1628] flex items-center gap-2">
              <Tags className="h-6 w-6" /> Categorias
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Categorize lançamentos financeiros e despesas de cartão.
            </p>
          </div>
          <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-[#0A1628]" data-testid="btn-nova-categoria">
            <Plus className="h-4 w-4 mr-1" /> Nova Categoria
          </Button>
        </div>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Cor</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map(c => {
                  const tipoCfg = TIPOS.find(t => t.value === c.tipo);
                  return (
                    <TableRow key={c.id} data-testid={`row-categoria-${c.id}`}>
                      <TableCell className="font-medium text-[#0A1628] flex items-center gap-2">
                        <span className="h-3 w-3 rounded-full border" style={{ background: c.cor || "#cbd5e1" }} />
                        {c.nome}
                      </TableCell>
                      <TableCell>
                        <Badge className={tipoCfg?.color || ""}>{tipoCfg?.label || c.tipo}</Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{c.cor || "-"}</TableCell>
                      <TableCell className="text-sm text-slate-600 max-w-md truncate">{c.descricao || "-"}</TableCell>
                      <TableCell>
                        <Badge variant={c.ativo ? "default" : "outline"}>{c.ativo ? "Ativo" : "Inativo"}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`btn-edit-${c.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-500" onClick={() => setDeleteItem(c)} data-testid={`btn-del-${c.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {items.length === 0 && (
              <p className="p-8 text-center text-sm text-slate-400">Nenhuma categoria cadastrada</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialog Form */}
      <Dialog open={showForm} onOpenChange={(o) => { if (!o) { setShowForm(false); resetForm(); } }}>
        <DialogContent className="max-w-md" data-testid="dialog-categoria-form">
          <DialogHeader>
            <DialogTitle>{editId ? "Editar Categoria" : "Nova Categoria"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                placeholder="Ex.: Alimentação"
                data-testid="input-nome-categoria"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label>Tipo *</Label>
              <div className="flex gap-2">
                {TIPOS.map(t => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setForm({ ...form, tipo: t.value })}
                    className={`flex-1 py-2 rounded-md text-xs font-medium border transition-colors ${
                      form.tipo === t.value ? `${t.color} border-current` : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
                    }`}
                    data-testid={`tipo-${t.value}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Cor</Label>
              <div className="flex flex-wrap gap-2">
                {COR_PALETA.map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setForm({ ...form, cor: c })}
                    className={`h-8 w-8 rounded-full border-2 transition-transform ${form.cor === c ? "scale-110 border-slate-800" : "border-white"}`}
                    style={{ background: c }}
                    title={c}
                  />
                ))}
                <Input
                  type="color"
                  value={form.cor}
                  onChange={(e) => setForm({ ...form, cor: e.target.value })}
                  className="w-10 h-10 p-0 border-0 cursor-pointer"
                  data-testid="input-cor-custom"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Descrição</Label>
              <Textarea
                value={form.descricao}
                onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                rows={2}
                placeholder="Opcional"
              />
            </div>
            {editId && (
              <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                <Label className="text-sm">Categoria ativa</Label>
                <input
                  type="checkbox"
                  checked={form.ativo}
                  onChange={(e) => setForm({ ...form, ativo: e.target.checked })}
                  className="h-4 w-4"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowForm(false); resetForm(); }}>Cancelar</Button>
            <Button onClick={handleSave} className="bg-[#0A1628]" data-testid="btn-save-categoria">
              {editId ? "Atualizar" : "Criar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmar exclusão */}
      <Dialog open={!!deleteItem} onOpenChange={(o) => !o && setDeleteItem(null)}>
        <DialogContent className="max-w-md" data-testid="dialog-del-categoria">
          <DialogHeader>
            <DialogTitle className="text-red-600">Remover categoria?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            "{deleteItem?.nome}" será marcada como removida (soft delete). Lançamentos já categorizados não são afetados.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteItem(null)}>Cancelar</Button>
            <Button className="bg-red-600" onClick={handleDelete} data-testid="btn-confirm-del">Remover</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
